import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { environment } from 'environments/environment';
// Plugins
import { SessionStorage, SessionStorageService } from 'ngx-webstorage';
import { Subscription, timer } from 'rxjs';
import moment from 'moment';
// Models
import { AuthData } from '@models/auth/auth.model.ro';
// Shared
import { APP_VERSION, BUILD_VERSION } from '@shared/config';
import { SiderMenu } from '@shared/interfaces';
// Services
import { AuthService }  from '@services/auth/auth.service';
import { PaymentService }  from '@services/payment/payment.service';

@Component({
	selector: 'sf-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss']
})
export class AppComponent implements AfterViewInit, OnInit, OnDestroy {

	@SessionStorage() public authData: AuthData;
	@SessionStorage() public currentRoute: string;
    @SessionStorage() public previousRoute: string;

	public testMode: boolean = !environment.production;
	public appVersion: string = APP_VERSION;
	public buildVersion: string = BUILD_VERSION;
	public year: string = moment().format('YYYY');
	public menuItems: Array<SiderMenu> = [];
	public spinnerState: boolean = false;
	private spinner$: Subscription = null;

	public constructor(
		private router: Router,
		private sessionStorageService: SessionStorageService,
		private authService: AuthService,
		private paymentService: PaymentService
	) {}

	public ngOnInit(): void {
		this.detectRouterChange();

		this.sessionStorageService.clear('googleApiLoaded');
	}

	public ngOnDestroy(): void {
		this.spinner$?.unsubscribe();
	}

	public ngAfterViewInit(): void {
		timer(50).subscribe(() => this.setMenuItems());
	}

	public isMenuSelected(data: string): boolean {
		data = data?.replace(/ /g, '-')?.toLowerCase();

		if (this.currentRoute?.replace('/', '')?.split('?')?.[0] == data) return true

		return false;
	}

	public navigateToPage(path: string): void {
		if (path == 'logout') return this.authService.logout();

		this.router.navigateByUrl(path)
	}

	private setMenuItems(): void {
		this.menuItems = [
			{ 
				title: 'H', 
				items: [
					{
						icon: 'home',
						title: 'Home',
						path: 'home'
					}
				] 
			},
			{ 
				title: 'A', 
				items: [
					{
						icon: 'idcard',
						title: 'Account',
						path: 'account'
					},
					{
						icon: 'contacts',
						title: 'Organization Mapping',
						path: 'organization-mapping'
					},
					{
						icon: 'apartment',
						title: 'Groups',
						path: 'groups'
					},
					{
						icon: 'usergroup-add',
						title: 'Group Users',
						path: 'group-users'
					},
					{
						icon: 'user',
						title: 'Users',
						path: 'users'
					},
					{
						icon: 'swap',
						title: 'Task Move',
						path: 'task-move'
					}
				] 
			},
			{
				title: 'P',
				hasRestriction: true,
				items: [
					{
						icon: 'credit-card',
						title: 'Credit Card Payment',
						path: 'credit-card-payment',
						hidden: true
					},
					{
						icon: 'credit-card',
						title: 'Stripe Payment',
						path: 'stripe-payment',
						hidden: true
					},
					{
						icon: 'pay-circle',
						title: 'Paywall',
						path: 'paywall',
						hidden: true
					},
					{
						icon: 'file-done',
						title: 'Hubspot Deals',
						path: 'hubspot-deals'
					}
				]
			},
			{
				title: 'S',
				items: [
					{
						icon: 'database',
						title: 'Support Logs',
						path: 'support-logs'
					},
					{
						icon: 'profile',
						title: 'Onboard',
						path: 'onboard'
					}
				]
			},
			{
				title: 'O',
				items: [
					{
						icon: 'environment',
						title: 'Analytics',
						path: 'analytics'
					},
					{
						icon: 'solution',
						title: 'Account Dashboard',
						path: 'account-dashboard'
					},
					{
						icon: 'safety',
						title: 'Security Dashboard',
						path: 'security-dashboard'
					},
					{
						icon: 'tool',
						title: 'Guestline',
						path: 'guestline'
					},
				]
			},
			{
				title: 'C',
				hasRestriction: true,
				items: [
					{
						icon: 'table',
						title: 'CS Dashboard',
						path: 'cs-dashboard'
					},
					{
						icon: 'notification',
						title: 'Marketing Popup',
						path: 'marketing-popup'
					},
					{
						icon: 'qrcode',
						title: 'QR Management',
						path: 'qr-management'
					},
					{
						icon: 'file-done',
						title: 'Survey',
						path: 'survey'
					},
					{
						icon: 'mail',
						title: 'SMS/E-mail Delivery',
						path: 'sms-email-delivery'
					}
				]
			},
			{ 
				title: 'L', 
				items: [
					{
						icon: 'logout',
						title: 'Log Out',
						path: 'logout'
					}
				] 
			}
		];

		this.checkRestrictionAccess();
	}

	private detectRouterChange(): void {
        this.router.events.subscribe((event: Event): void | boolean => {
            if (event instanceof NavigationEnd) {
                if (this.currentRoute == event.urlAfterRedirects) return false;

                this.previousRoute = this.currentRoute;
                this.currentRoute = event.urlAfterRedirects;

				this.checkRestrictionAccess();
            }
        });
    }

	private checkRestrictionAccess(): void {
		let availableRoutes: Array<string> = ['credit-card-payment', 'stripe-payment', 'paywall', 'survey'];

		for (let menu of this.menuItems) {
			if (!menu?.hasRestriction) continue;

			for (let item of menu?.items) {
				if (this.paymentService.checkRestrictionAccess()) item.hidden = false;
				else {
					if (availableRoutes?.includes(item?.path)) item.hidden = true;
				}
			}
		}
	}
}